/**
 * ParkingSpotSelector
 *
 * Zone-grouped parking-spot grid. When the tenant has an uploaded yard map and
 * spots placed on it (#3286), the map is shown first and the grid below lists
 * only the spots that are not on it. Dense markers can overlap, so a toggle
 * expands that grid to every spot as a list alternative to the map; the
 * selection is shared. Without a usable map (none, still loading, image error,
 * no placed spots, older server) the full grid is the whole selector, exactly
 * as before. All paths emit the same two callbacks.
 * Used in PauseWorkDialog and VehicleLocationDialog.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  useParkingCurrentStatus,
  type ParkingSpotStatus,
  type ParkingZoneStatus,
  type YardMapSummary,
} from '@/hooks/useParking';
import { spotMapPosition, useYardMapImage } from '@/hooks/useYardMap';
import { YardMapCanvas, type YardMapMarker } from '@/components/parking/YardMapCanvas';

interface ParkingSpotSelectorProps {
  selectedZoneId: string;
  selectedSpotId: string;
  onZoneChange: (zoneId: string) => void;
  onSpotChange: (spotId: string) => void;
}

function sortSpots(spots: ParkingSpotStatus[]): ParkingSpotStatus[] {
  return spots
    .slice()
    .sort((a, b) => a.identifier.localeCompare(b.identifier, undefined, { numeric: true }));
}

function spotStatusLabel(
  t: (key: string, options?: Record<string, string>) => string,
  spot: ParkingSpotStatus,
  selected: boolean,
): string {
  if (spot.isOccupied) {
    return spot.vehicleReg
      ? t('vehicleLocation.spotOccupiedBy', { reg: spot.vehicleReg })
      : t('vehicleLocation.spotOccupied');
  }
  return selected
    ? t('vehicleLocation.spotSelected')
    : t('vehicleLocation.spotAvailable');
}

function spotAriaLabel(
  t: (key: string, options?: Record<string, string>) => string,
  spot: ParkingSpotStatus,
  selected: boolean,
): string {
  if (spot.isOccupied) {
    return spot.vehicleReg
      ? t('vehicleLocation.spotAriaOccupiedBy', {
          identifier: spot.identifier,
          reg: spot.vehicleReg,
        })
      : t('vehicleLocation.spotAriaOccupied', { identifier: spot.identifier });
  }
  return selected
    ? t('vehicleLocation.spotAriaSelected', { identifier: spot.identifier })
    : t('vehicleLocation.spotAriaAvailable', { identifier: spot.identifier });
}

type Translate = (key: string, options?: Record<string, string>) => string;

function markerStatusLabel(t: Translate, spot: ParkingSpotStatus, selected: boolean): string {
  if (spot.isOccupied) return t('vehicleLocation.spotOccupied');
  return selected ? t('vehicleLocation.spotSelected') : t('vehicleLocation.spotAvailable');
}

interface SpotSelection {
  selectedZoneId: string;
  selectedSpotId: string;
  onSelect: (zoneId: string, spotId: string) => void;
}

/** The zone-grouped button grid; with `hideMapped` it lists only spots that are not on the map. */
function ZoneGrid({
  zones,
  hideMapped,
  selectedZoneId,
  selectedSpotId,
  onSelect: handleSelect,
}: SpotSelection & { zones: ParkingZoneStatus[]; hideMapped: boolean }) {
  const { t } = useTranslation('workorders');
  return (
    <>
      {zones.map((zone) => {
        const spots = sortSpots(zone.spots).filter((spot) => !hideMapped || spotMapPosition(spot) === null);
        // With the map shown, a zone whose spots are all on it has nothing left to list.
        if (hideMapped && spots.length === 0 && zone.spots.length > 0) return null;
        return (
          <Box key={zone.zoneId} role="group" aria-label={zone.zoneName}>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
              {zone.zoneName}
            </Typography>
            {spots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('pauseDialog.noSpotsAvailable')}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(5.5rem, 1fr))',
                  gap: 1,
                }}
              >
                {spots.map((spot) => {
                  const selected =
                    selectedSpotId === spot.spotId && selectedZoneId === zone.zoneId;
                  const occupied = spot.isOccupied;
                  return (
                    <Button
                      key={spot.spotId}
                      type="button"
                      variant={selected ? 'contained' : 'outlined'}
                      color={occupied ? 'error' : 'success'}
                      disabled={occupied}
                      aria-pressed={selected}
                      aria-label={spotAriaLabel(t, spot, selected)}
                      onClick={() => handleSelect(zone.zoneId, spot.spotId)}
                      sx={{
                        width: '100%',
                        minHeight: 56,
                        px: 0.75,
                        py: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                        textTransform: 'none',
                        lineHeight: 1.2,
                        borderWidth: selected ? 2 : 1,
                        '&.Mui-disabled': {
                          opacity: 1,
                          color: 'text.secondary',
                          borderColor: 'error.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>
                        {spot.identifier}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ maxWidth: '100%', color: 'inherit', lineHeight: 1.2 }}
                      >
                        {spotStatusLabel(t, spot, selected)}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

/**
 * Only mounted when the map has placed spots, so the picture query (and its
 * QueryClient dependency) is never touched for a tenant without a map.
 */
function YardMapSelector({
  zones,
  yardMap,
  ...selection
}: SpotSelection & { zones: ParkingZoneStatus[]; yardMap: YardMapSummary }) {
  const { t } = useTranslation('workorders');
  const { selectedZoneId, selectedSpotId, onSelect } = selection;
  const image = useYardMapImage(yardMap);
  // A picture the browser cannot decode falls back to the list for this revision only.
  const revisionKey = `${yardMap.id}:${String(yardMap.generation)}`;
  const [failedRevision, setFailedRevision] = useState<string | null>(null);
  // Overlapping markers must not be the only way to a spot: the whole list can be opened.
  const [showAll, setShowAll] = useState(false);
  const listId = useId();
  const showMap = image.status === 'ready' && image.url !== null && failedRevision !== revisionKey;

  const markers: YardMapMarker[] = [];
  const zoneOfSpot = new Map<string, string>();
  if (showMap) {
    for (const zone of zones) {
      for (const spot of sortSpots(zone.spots)) {
        zoneOfSpot.set(spot.spotId, zone.zoneId);
        const position = spotMapPosition(spot);
        if (!position) continue;
        const selected = selectedSpotId === spot.spotId && selectedZoneId === zone.zoneId;
        markers.push({
          id: spot.spotId,
          identifier: spot.identifier,
          zoneName: zone.zoneName,
          x: position.x,
          y: position.y,
          tone: spot.isOccupied ? 'occupied' : 'free',
          statusLabel: markerStatusLabel(t, spot, selected),
          ariaLabel: t('vehicleLocation.mapMarkerAria', {
            label: spotAriaLabel(t, spot, selected),
            zone: zone.zoneName,
          }),
          selected,
          disabled: spot.isOccupied,
        });
      }
    }
  }
  const hasUnmapped = zones.some((zone) => zone.spots.some((spot) => spotMapPosition(spot) === null));

  return (
    <>
      {showMap && image.url && (
        <YardMapCanvas
          imageUrl={image.url}
          widthPx={yardMap.widthPx}
          heightPx={yardMap.heightPx}
          imageAlt={t('vehicleLocation.yardMapAlt')}
          groupLabel={t('vehicleLocation.yardMapLabel')}
          markers={markers}
          mode="select"
          onSelect={(spotId) => {
            const zoneId = zoneOfSpot.get(spotId);
            if (zoneId) onSelect(zoneId, spotId);
          }}
          onImageError={() => setFailedRevision(revisionKey)}
        />
      )}
      {showMap && (
        <Button
          type="button"
          size="small"
          variant="text"
          aria-expanded={showAll}
          aria-controls={listId}
          onClick={() => setShowAll((open) => !open)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {showAll ? t('vehicleLocation.hideAllSpots') : t('vehicleLocation.showAllSpots')}
        </Button>
      )}
      <Stack id={listId} spacing={2.5} sx={{ '&:empty': { display: 'none' } }}>
        {showMap && (showAll || hasUnmapped) && (
          <Typography component="h3" variant="subtitle2">
            {showAll ? t('vehicleLocation.allSpots') : t('vehicleLocation.unmappedSpots')}
          </Typography>
        )}
        <ZoneGrid zones={zones} hideMapped={showMap && !showAll} {...selection} />
      </Stack>
    </>
  );
}

export function ParkingSpotSelector({
  selectedZoneId,
  selectedSpotId,
  onZoneChange,
  onSpotChange,
}: ParkingSpotSelectorProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { data, isLoading, isError } = useParkingCurrentStatus();

  const handleSelect = (zoneId: string, spotId: string) => {
    onZoneChange(zoneId);
    onSpotChange(spotId);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={28} aria-label={tc('loading')} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography variant="body2" color="error">
        {tc('error')}
      </Typography>
    );
  }

  const zones = data?.zones ?? [];

  if (zones.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('vehicleLocation.noParkingZones')}
      </Typography>
    );
  }

  // Older servers send no yardMap and no coordinates: that is the plain grid.
  const yardMap = data?.yardMap ?? null;
  const hasPlacedSpots = zones.some((zone) => zone.spots.some((spot) => spotMapPosition(spot) !== null));
  const selection = { selectedZoneId, selectedSpotId, onSelect: handleSelect };

  return (
    <Stack spacing={2.5} role="group" aria-label={t('vehicleLocation.selectParkingSpot')}>
      {yardMap !== null && hasPlacedSpots ? (
        <YardMapSelector zones={zones} yardMap={yardMap} {...selection} />
      ) : (
        <ZoneGrid zones={zones} hideMapped={false} {...selection} />
      )}
    </Stack>
  );
}
